import React, { useEffect, useState } from 'react';
import { Modal, Spin, Alert, Row, Col, Divider } from 'antd';
import styled from 'styled-components';
import AvatarBig from 'components/AvatarBig';
import employeeApi from 'api/employeeApi';

interface PayrollEmployeeDetailModalProps {
  isVisible: boolean;
  employeeId: string | number;
  onClose: () => void;
  preloadedEmployeeData?: any; // Optional pre-loaded employee data
}

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
  }

  .ant-modal-header {
    background-color: #f5f5f5;
    border-bottom: 1px solid #e8e8e8;
    padding: 16px 24px;
  }

  .ant-modal-body {
    padding: 24px;
  }
`;

const EmployeeHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
`;

const EmployeeInfo = styled.div`
  margin-left: 16px;
`;

const EmployeeName = styled.h2`
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 600;
`;

const EmployeePosition = styled.div`
  color: #8c8c8c;
  font-size: 14px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #262626;
`;

const InfoRow = styled.div`
  display: flex;
  margin-bottom: 12px;
`;

const InfoLabel = styled.div`
  width: 150px;
  color: #8c8c8c;
  font-size: 14px;
`;

const InfoValue = styled.div`
  flex: 1;
  font-size: 14px;
  color: #262626;
`;

const InfoCard = styled.div`
  background-color: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
`;

const PayrollEmployeeDetailModal: React.FC<PayrollEmployeeDetailModalProps> = ({
  isVisible,
  employeeId,
  onClose,
  preloadedEmployeeData,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [employeeData, setEmployeeData] = useState<any>(null);

  // Helper function to safely extract string values from potentially complex objects
  const safelyExtractValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value.toString();

    // Handle objects with name property (common in API responses)
    if (typeof value === 'object' && value !== null) {
      if (value.name) return value.name;
      if (value.label) return value.label;
      if (value.value && (typeof value.value === 'string' || typeof value.value === 'number')) {
        return value.value.toString();
      }
    }

    return '';
  };

  useEffect(() => {
    const fetchEmployeeData = async () => {
      // Always fetch complete data from API when modal is visible
      if (isVisible && employeeId) {
        setLoading(true);
        setError(null);

        try {
          // Use preloaded data initially for faster rendering
          if (preloadedEmployeeData) {
            console.log('Using preloaded employee data initially:', preloadedEmployeeData);
            setEmployeeData(preloadedEmployeeData);
          }

          // Then fetch complete data from API
          const response = await employeeApi.getEmployeeDetail(employeeId);
          if (response?.data) {
            console.log('API employee data:', response.data);

            // Process API data to ensure all fields are accessible and safe for rendering
            const processedData = {
              ...response.data,
              // Extract department name from departments array if available
              departmentName:
                response.data.departments && response.data.departments.length > 0
                  ? safelyExtractValue(response.data.departments[0].name)
                  : safelyExtractValue(response.data.departmentName),
              // Extract manager name from managers array if available
              managerName:
                response.data.managers && response.data.managers.length > 0
                  ? safelyExtractValue(response.data.managers[0].fullName)
                  : safelyExtractValue(response.data.managerName),
              // Extract gender from sex object if available
              gender:
                safelyExtractValue(response.data.sex) || safelyExtractValue(response.data.sexValue),
              // Map date of birth
              dateOfBirth: safelyExtractValue(response.data.dob),
              // Map insurance
              insurance: safelyExtractValue(response.data.socialInsuranceNumber),
              // Safely extract other potentially complex fields
              nationality: safelyExtractValue(response.data.nationality),
              maritalStatus: safelyExtractValue(response.data.maritalStatus),
              contractType: safelyExtractValue(response.data.contractType),
              employmentType: safelyExtractValue(response.data.employmentType),
              level: safelyExtractValue(response.data.level),
              status:
                safelyExtractValue(response.data.status) ||
                safelyExtractValue(response.data.employeeStatus),
              idNumber: safelyExtractValue(response.data.employeeId),
              address: safelyExtractValue(response.data.permanentAddress),
            };

            // Merge API data with preloaded data to ensure we have the most complete information
            const mergedData = preloadedEmployeeData
              ? {
                  ...preloadedEmployeeData,
                  ...processedData,
                  // Preserve payroll specific data from preloaded data
                  totalSalaryCost:
                    preloadedEmployeeData.totalSalaryCost || processedData.totalSalaryCost,
                  actualReceiveSalary:
                    preloadedEmployeeData.actualReceiveSalary || processedData.actualReceiveSalary,
                  insurance: preloadedEmployeeData.insurance || processedData.insurance,
                  totalGrossSalary:
                    preloadedEmployeeData.totalGrossSalary || processedData.totalGrossSalary,
                  overTime: preloadedEmployeeData.overTime || processedData.overTime,
                  tax: preloadedEmployeeData.tax || processedData.tax,
                }
              : processedData;

            setEmployeeData(mergedData);
          } else if (!preloadedEmployeeData) {
            // Only show error if we don't have preloaded data
            setError('Could not fetch employee details');
          }
        } catch (err) {
          // If we have preloaded data, continue showing that even if API call fails
          if (!preloadedEmployeeData) {
            setError('Error fetching employee details');
          }
          console.error('Error fetching employee details:', err);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchEmployeeData();
  }, [isVisible, employeeId, preloadedEmployeeData]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isVisible) {
      setError(null);
    }
  }, [isVisible]);

  const renderContactInfo = () => {
    if (!employeeData) return null;

    return (
      <>
        <SectionTitle>Contact Information</SectionTitle>
        <InfoCard>
          <InfoRow>
            <InfoLabel>Email</InfoLabel>
            <InfoValue>{employeeData.email || employeeData.personalEmail || 'N/A'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Phone</InfoLabel>
            <InfoValue>{employeeData.phoneNumber || 'N/A'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Work Phone</InfoLabel>
            <InfoValue>{employeeData.workPhone || 'N/A'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Emergency Contact</InfoLabel>
            <InfoValue>
              {employeeData.emergencyContact ||
                (employeeData.relative && employeeData.relative.length > 0
                  ? employeeData.relative
                      .filter((r) => r.relationshipType === 'Emergency Contact')
                      .map((r) => `${r.fullName} (${r.phoneNumber || 'No phone'})`)
                      .join(', ')
                  : 'N/A')}
            </InfoValue>
          </InfoRow>
        </InfoCard>
      </>
    );
  };

  // Helper function to format date values
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (e) {
      return dateString;
    }
  };

  const renderEmploymentDetails = () => {
    if (!employeeData) return null;

    // Fetch manager data if needed
    const fetchManagerData = async () => {
      if (employeeData.managerId && !employeeData.managerName && !employeeData.manager?.fullName) {
        try {
          const response = await employeeApi.getManager(employeeData.managerId);
          if (response?.data) {
            setEmployeeData((prev) => ({
              ...prev,
              manager: {
                ...prev.manager,
                fullName: response.data.fullName,
              },
            }));
          }
        } catch (err) {
          console.error('Error fetching manager details:', err);
        }
      }
    };

    // Call fetchManagerData if needed
    if (employeeData.managerId && !employeeData.managerName && !employeeData.manager?.fullName) {
      fetchManagerData();
    }

    return (
      <>
        <SectionTitle>Employment Details</SectionTitle>
        <InfoCard>
          <InfoRow>
            <InfoLabel>Employee ID</InfoLabel>
            <InfoValue>{employeeData.employeeId || employeeData.id || 'N/A'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Department</InfoLabel>
            <InfoValue>
              {employeeData.department?.name ||
                employeeData.departmentName ||
                (typeof employeeData.line === 'string' ? employeeData.line : null) ||
                'N/A'}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Position</InfoLabel>
            <InfoValue>
              {employeeData.position?.name ||
                employeeData.positionName ||
                employeeData.position ||
                employeeData.roleName ||
                'N/A'}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Manager</InfoLabel>
            <InfoValue>
              {employeeData.manager?.fullName || employeeData.managerName || 'N/A'}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Start Date</InfoLabel>
            <InfoValue>{formatDate(employeeData.startDate || employeeData.hireDate)}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Employment Type</InfoLabel>
            <InfoValue>
              {employeeData.employmentType || employeeData.contractType || 'N/A'}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Status</InfoLabel>
            <InfoValue>{employeeData.status || employeeData.employmentStatus || 'N/A'}</InfoValue>
          </InfoRow>
        </InfoCard>
      </>
    );
  };

  const renderPayrollInfo = () => {
    if (!employeeData) return null;

    // Helper function to format currency values
    const formatCurrency = (value: any) => {
      if (value === undefined || value === null) return 'N/A';
      if (typeof value === 'number') {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(value);
      }
      return value.toString();
    };

    return (
      <>
        <SectionTitle>Payroll Information</SectionTitle>
        <InfoCard>
          <InfoRow>
            <InfoLabel>Gross Salary</InfoLabel>
            <InfoValue>
              {formatCurrency(
                employeeData.totalGrossSalary || employeeData.grossSalary || employeeData.salary,
              )}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Net Salary</InfoLabel>
            <InfoValue>
              {formatCurrency(employeeData.actualReceiveSalary || employeeData.netSalary)}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Insurance</InfoLabel>
            <InfoValue>{formatCurrency(employeeData.insurance)}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Tax</InfoLabel>
            <InfoValue>{formatCurrency(employeeData.tax)}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Overtime</InfoLabel>
            <InfoValue>{formatCurrency(employeeData.overTime)}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Total Cost</InfoLabel>
            <InfoValue>{formatCurrency(employeeData.totalSalaryCost)}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Bank Account</InfoLabel>
            <InfoValue>{employeeData.bankAccount || 'N/A'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Bank Name</InfoLabel>
            <InfoValue>{employeeData.bankName || 'N/A'}</InfoValue>
          </InfoRow>
        </InfoCard>
      </>
    );
  };

  const renderPersonalInfo = () => {
    if (!employeeData) return null;

    return (
      <>
        <SectionTitle>Personal Information</SectionTitle>
        <InfoCard>
          <InfoRow>
            <InfoLabel>Date of Birth</InfoLabel>
            <InfoValue>
              {formatDate(employeeData.dateOfBirth || employeeData.birthDate || employeeData.dob)}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Gender</InfoLabel>
            <InfoValue>
              {typeof employeeData.gender === 'string'
                ? employeeData.gender === 'M' || employeeData.gender.toLowerCase() === 'male'
                  ? 'Male'
                  : employeeData.gender === 'F' || employeeData.gender.toLowerCase() === 'female'
                  ? 'Female'
                  : employeeData.gender
                : 'N/A'}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Nationality</InfoLabel>
            <InfoValue>{employeeData.nationality || 'N/A'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Marital Status</InfoLabel>
            <InfoValue>{employeeData.maritalStatus || 'N/A'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>ID Number</InfoLabel>
            <InfoValue>{employeeData.idNumber || employeeData.employeeId || 'N/A'}</InfoValue>
          </InfoRow>
        </InfoCard>
      </>
    );
  };

  const renderAddressInfo = () => {
    if (!employeeData) return null;

    return (
      <>
        <SectionTitle>Address Information</SectionTitle>
        <InfoCard>
          <InfoRow>
            <InfoLabel>Address</InfoLabel>
            <InfoValue>{employeeData.address || 'N/A'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>City</InfoLabel>
            <InfoValue>{employeeData.city || 'N/A'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>State/Province</InfoLabel>
            <InfoValue>{employeeData.state || employeeData.province || 'N/A'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Postal Code</InfoLabel>
            <InfoValue>{employeeData.postalCode || employeeData.zipCode || 'N/A'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Country</InfoLabel>
            <InfoValue>{employeeData.country || 'N/A'}</InfoValue>
          </InfoRow>
        </InfoCard>
      </>
    );
  };

  const renderEducationInfo = () => {
    if (!employeeData || !employeeData.education) return null;

    return (
      <>
        <SectionTitle>Education</SectionTitle>
        <InfoCard>
          <InfoRow>
            <InfoLabel>Highest Degree</InfoLabel>
            <InfoValue>{employeeData.education?.highestDegree || 'N/A'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Institution</InfoLabel>
            <InfoValue>{employeeData.education?.institution || 'N/A'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Field of Study</InfoLabel>
            <InfoValue>{employeeData.education?.fieldOfStudy || 'N/A'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Graduation Year</InfoLabel>
            <InfoValue>{employeeData.education?.graduationYear || 'N/A'}</InfoValue>
          </InfoRow>
        </InfoCard>
      </>
    );
  };

  const renderSkillsInfo = () => {
    if (!employeeData || !employeeData.skills || employeeData.skills.length === 0) return null;

    return (
      <>
        <SectionTitle>Skills</SectionTitle>
        <InfoCard>
          <InfoRow>
            <InfoValue>
              {Array.isArray(employeeData.skills)
                ? employeeData.skills.join(', ')
                : employeeData.skills || 'N/A'}
            </InfoValue>
          </InfoRow>
        </InfoCard>
      </>
    );
  };

  // Determine if we should show the loading state
  // Only show loading if we have no data at all
  const showLoading = loading && !employeeData;

  return (
    <StyledModal
      title="Employee Details"
      open={isVisible}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
      centered
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert message="Error" description={error} type="error" showIcon />
      ) : employeeData ? (
        <>
          <EmployeeHeader>
            <AvatarBig
              size={80}
              fontSize={'24px'}
              firstName={employeeData.firstName || employeeData.fullName?.split(' ')[0] || ''}
              lastName={
                employeeData.lastName || employeeData.fullName?.split(' ').slice(1).join(' ') || ''
              }
              url={employeeData.avatar || employeeData.profilePicture}
            />
            <EmployeeInfo>
              <EmployeeName>
                {employeeData.fullName ||
                  `${employeeData.firstName || ''} ${employeeData.lastName || ''}`}
              </EmployeeName>
              <EmployeePosition>
                {employeeData.position?.name ||
                  employeeData.positionName ||
                  employeeData.roleName ||
                  'Employee'}
              </EmployeePosition>
            </EmployeeInfo>
          </EmployeeHeader>

          <Divider />

          <Row gutter={[24, 0]}>
            <Col span={24}>{renderContactInfo()}</Col>
          </Row>

          <Row gutter={[24, 0]}>
            <Col span={24}>{renderEmploymentDetails()}</Col>
          </Row>

          <Row gutter={[24, 0]}>
            <Col span={24}>{renderPayrollInfo()}</Col>
          </Row>

          <Row gutter={[24, 0]}>
            <Col span={12}>{renderPersonalInfo()}</Col>
            <Col span={12}>{renderAddressInfo()}</Col>
          </Row>

          <Row gutter={[24, 0]}>
            <Col span={12}>{renderEducationInfo()}</Col>
            <Col span={12}>{renderSkillsInfo()}</Col>
          </Row>
        </>
      ) : (
        <Alert message="No employee data available" type="warning" showIcon />
      )}
    </StyledModal>
  );
};

export default PayrollEmployeeDetailModal;
